import { UseMutateFunction } from 'react-query';
import { Company, CompanyRole, StateProvince } from './company';
import { Schedule } from './schedule';
import * as yup from 'yup';
import { MultiSelectOption } from './CommonTypes';
import {
  UseFormSetError,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { Ability } from '@casl/ability';
import { Actions, Subjects } from './permission';
import { endOfDay, parseISO, startOfDay } from 'date-fns';

export enum AttributeSelection {
  Single = 0, //single attribute
  Group = 1, //group attribute
}

export interface SelectedAttributeGroup {
  id: string;
  startDate?: string | null;
  endDate?: string | null;
  displayName: string;
}

export interface Resource {
  id?: string;
  resourceGuid?: string;
  resourceName?: string;
  tenantId: string;
  createdAt?: string;
  updatedAt?: string;
  integrationGUID: string;
  isContingent?: boolean;
  contingentCompany?: string | null;
  isActive?: boolean | number;
  isAlwaysAvailable?: boolean;
  name: string;
  description?: string;
  email: string;
  phone?: string;
  commuteTimeInMin?: number;
  wipLimit?: number;
  claimCapacity?: number;
  claimOwnerCapacity?: ClaimOwnerCapacity;
  fieldCapacityByDay?: FieldCapacityByDay;
  fieldCapacityMode?: FieldCapacityMode;
  virtualClaimCapacity?: number;
  complexLossClaimCapacity?: number;
  efficiency?: number;
  companies: CompanyRef[];
  company?: Company;
  offices: { integrationGUID: string }[];
  teams: { integrationGUID: string }[];
  groups: { integrationGUID: string }[];
  roles: CompanyRole[];
  locations: Location[];
  selectedAttributeDefinitions: SelectedAttributeDefinitions[];
  selectedAttributeGroups?: SelectedAttributeGroup[];
  attributeFilterSelection?: AttributeSelection;
  selectedCertificationsAndLicenses: SelectedCertificationsAndLicenses[];
  isWorkTeam?: boolean;
  lockImmediately?: boolean;
  isStandby?: boolean;
  cwsUserId?: string;
  metadata?: Record<string, string>;
}

export interface ClaimOwnerCapacity {
  onMonday: number;
  onTuesday: number;
  onWednesday: number;
  onThursday: number;
  onFriday: number;
  onSaturday: number;
  onSunday: number;
  maxWeeklyCapacity: number;
  initialOnMonday?: number;
  initialOnTuesday?: number;
  initialOnWednesday?: number;
  initialOnThursday?: number;
  initialOnFriday?: number;
  initialOnSaturday?: number;
  initialOnSunday?: number;
  initialMaxWeeklyCapacity?: number;
}

/**
 * Per-day field capacity for a resource. Mirrors `ClaimOwnerCapacity` minus
 * the weekly-cap concept -- field capacity is a straight per-day value with no
 * "max weekly capacity" ceiling.
 */
/**
 * Which form of field capacity a single resource resolves from (ADO #94651).
 *
 * These are the exact strings the API serializes and accepts -- the server enum is annotated
 * `[JsonConverter(typeof(StringEnumConverter))]` with `[EnumMember(Value = "STATIC")]` /
 * `[EnumMember(Value = "PER_DAY")]`. Do not use "Static"/"PerDay" or the ordinals 0/1.
 *
 * `undefined` means "not specified"; the server treats that as "leave whatever is stored" and
 * resolves it as STATIC. It is deliberately NOT coerced to STATIC on a write, because doing so
 * would demote a resource a dispatcher had configured for per-day capacity.
 */
export type FieldCapacityMode = 'STATIC' | 'PER_DAY';

export interface FieldCapacityByDay {
  onMonday: number;
  onTuesday: number;
  onWednesday: number;
  onThursday: number;
  onFriday: number;
  onSaturday: number;
  onSunday: number;
  initialOnMonday?: number;
  initialOnTuesday?: number;
  initialOnWednesday?: number;
  initialOnThursday?: number;
  initialOnFriday?: number;
  initialOnSaturday?: number;
  initialOnSunday?: number;
}

/**
 * A date-specific override of a resource's field capacity, keyed by
 * resourceId + date. Used to bump/reduce capacity for a single day without
 * changing the resource's standing per-day-of-week capacity.
 */
export interface FieldCapacityOverride {
  resourceId: string;
  date: string;
  capacity: number;
  createdAt?: string;
  updatedAt?: string;
}

export type FieldCapacityOverridePost = {
  date: string;
  capacity: number;
};

export interface EditFieldCapacityOverrideForm {
  date: string | null;
  capacity: number;
}

export const editFieldCapacityOverrideFormDefault: EditFieldCapacityOverrideForm =
  {
    date: null,
    capacity: 0,
  };

export const editFieldCapacityOverrideFormSchema = yup.object().shape({
  date: yup
    .string()
    .nullable()
    .required('Date is required')
    .test('not-past-date', 'Date cannot be in the past', (value) => {
      if (!value) return true;
      return startOfDay(parseISO(value)) >= startOfDay(new Date());
    }),
  capacity: yup
    .number()
    .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
    .min(0, 'Capacity must be non-negative'),
});

export interface QualifiedResource {
  Id: string;
  ResourceName: string;
}

export interface CompanyRef {
  externalResourceId?: string;
  integrationGUID: string;
}
export interface Location {
  integrationGUID: string;
  name: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  county?: string;
  unit?: string;
  lat?: number;
  long?: number;
  startDate?: string;
  endDate?: string;
  allowedTravelRadius?: number;
  schedule?: Schedule[];
  monday?: boolean;
  tuesday?: boolean;
  wednesday?: boolean;
  thursday?: boolean;
  friday?: boolean;
  saturday?: boolean;
  sunday?: boolean;
  isStartingLocation?: boolean;
  isVirtualLocation?: boolean;
  exclusionZones?: ZoneInfo;
  inclusionZones?: ZoneInfo;
}

export interface ZoneInfo {
  states?: string[];
  counties?: string[];
  zipCodes?: string[];
}

export interface SubAttribute {
  id?: number;
  selectedAttributeDefinitionId?: number;
  displayName: string;
  attributeDefinitionValueId?: number;
  integrationGUID: string;
  value: string | undefined;
  selectedAttributeDefinitionIntegrationGUID: string;
  attributeDefinitionValueIntegrationGUID: string;
  startDate?: string;
  endDate?: string;
}

export interface SelectedAttributeValuesVarchars {
  id?: number;
  selectedAttributeDefinitionId?: number;
  displayName: string;
  attributeDefinitionValueId?: number;
  integrationGUID: string;
  value: string | undefined;
  selectedAttributeDefinitionIntegrationGUID: string;
  attributeDefinitionValueIntegrationGUID: string;
  startDate?: string;
  endDate?: string;
  subAttributes?: SubAttribute[];
}

export interface SelectedAttributeValuesInts {
  id: number;
  selectedAttributeDefinitionId: number;
  displayName: string;
  attributeDefinitionValueId: number;
  integrationGUID: string;
  value: number | undefined;
  selectedAttributeDefinitionIntegrationGUID: string;
  attributeDefinitionValueIntegrationGUID: string;
  startDate?: string;
  endDate?: string;
}

export interface SelectedAttributeValuesFloats {
  id: number;
  selectedAttributeDefinitionId: number;
  displayName: string;
  attributeDefinitionValueId: number;
  integrationGUID: string;
  value: number | undefined;
  selectedAttributeDefinitionIntegrationGUID: string;
  attributeDefinitionValueIntegrationGUID: string;
  startDate?: string;
  endDate?: string;
}

export interface SelectedAttributeValuesBools {
  id?: number;
  selectedAttributeDefinitionId?: number;
  displayName: string;
  attributeDefinitionValueId?: number;
  integrationGUID: string;
  value: boolean | undefined;
  selectedAttributeDefinitionIntegrationGUID: string;
  attributeDefinitionValueIntegrationGUID: string;
  startDate?: string;
  endDate?: string;
}
export interface SelectedAttributeDefinitions {
  integrationGUID: string;
  attributeDefinitionIntegrationGUID: string;
  displayName: string;
  id?: number;
  attributeDefinitionId?: number;
  selectedAttributeValuesVarchars: SelectedAttributeValuesVarchars[];
  selectedAttributeValuesInts: SelectedAttributeValuesInts[];
  selectedAttributeValuesFloats: SelectedAttributeValuesFloats[];
  selectedAttributeValuesBools: SelectedAttributeValuesBools[];
}
export interface SelectedCertificationsAndLicenses {
  integrationGUID: string;
  certificationAndLicenseIntegrationGUID: string;
  // Optional because the resource payload never sends it: a certification's name
  // lives only in the company catalog, joined client-side on
  // certificationAndLicenseIntegrationGUID. Typing it as required made every
  // consumer believe a name was present when it is always undefined.
  name?: string;
  verificationType: string;
  id?: number;
  organization: string;
  licenseNumber: string;
  verificationStatus: string;
  documentCount: number;
  issueDate: string;
  expirationDate: string | null;
}

export interface LocationForm {
  integrationGUID?: string;
  locationInformation?: LocationInformation;
  serviceInformation?: ServiceInformation;
  exclusionZonesInformation?: ZonesInformation;
  inclusionZonesInformation?: ZonesInformation;
}

export interface LocationFormBulk {
  integrationGUID?: string;
  locationInformation?: LocationInformation;
  serviceInformation?: ServiceInformationBulk;
  exclusionZonesInformation?: ZonesInformation;
  inclusionZonesInformation?: ZonesInformation;
}

export interface ZonesInformation {
  states?: string[];
  counties?: string[] | MultiSelectOption[];
  zipCodes?: string[];
}

export interface LocationInformation {
  name?: string;
  street?: string;
  unit?: string;
  city?: string;
  state?: string;
  zip?: string;
  county?: string;
  lat?: number;
  long?: number;
  isVirtualLocation?: string;
}

export interface ServiceInformation {
  startDate?: string;
  endDate?: string;
  allowedTravelRadius?: number;
  monday?: boolean;
  tuesday?: boolean;
  wednesday?: boolean;
  thursday?: boolean;
  friday?: boolean;
  saturday?: boolean;
  sunday?: boolean;
  isStartingLocation?: boolean;
  isVirtualLocation?: string;
}

export interface ServiceInformationBulk {
  startDate?: string;
  endDate?: string;
  allowedTravelRadius?: number;
  monday?: boolean;
  tuesday?: boolean;
  wednesday?: boolean;
  thursday?: boolean;
  friday?: boolean;
  saturday?: boolean;
  sunday?: boolean;
  isStartingLocation?: boolean;
  isVirtualLocation?: string;
}

export type ResourceBulkType = 'Resources' | 'Team' | 'Office' | 'Group';

export type GeneralInformation = {
  name: string;
  description?: string;
  externalResourceId: string;
  commuteTimeInMin?: number;
  wipLimit?: number;
  email: string;
  phone?: string;
  claimCapacity?: number;
  virtualClaimCapacity?: number;
  complexLossClaimCapacity?: number;
  efficiency?: number;
  isWorkTeam?: boolean;
  lockImmediately?: boolean;
  isStandby?: boolean;
  isAlwaysAvailable?: boolean;
};

export type TeamsOfficesRoles = {
  teams: string[];
  offices: string[];
  roles: string[];
  isContingent?: boolean;
  contingentCompany?: string;
};
export interface ResourceBulkForm {
  type?: ResourceBulkType;
  exclusionZonesInformation: {
    states?: string[];
    counties?: MultiSelectOption[];
    zipCodes?: string[];
  };
  inclusionZonesInformation: {
    states?: string[];
    counties?: MultiSelectOption[];
    zipCodes?: string[];
  };
  ids: string[];
  generalInformation: GeneralInformation;
  locations: LocationFormBulk[];
  teamsOfficesRoles: TeamsOfficesRoles;
  attributes: AttributeForm[];
  certsAndLicenses: CertAndLicensesForm[];
  addSelectedAttributeGUID: string;
  isNewResource: boolean;
  // Grouped-attribute bulk update. On the bulk Attributes tab the
  // user picks "attributes" OR "groups" up front; attributeKind tracks which kind
  // is staged so the kind-gate can disable switching until staged changes are cleared.
  selectedAttributeGroups?: SelectedAttributeGroup[];
  attributeFilterSelection?: AttributeSelection;
  attributeKind?: 'attributes' | 'groups' | null;
  addSelectedGroupAttributeGUID?: string;
  // All owner-capacity fields are optional: the bulk form defaults them to
  // undefined, and a copy source that has no owner capacity leaves them absent
  // (mirroring the Handler capacities) so the section is omitted from the
  // payload rather than zeroing every target.
  // No calculatedSumOfCapacities here (unlike the single-resource form): it is a
  // UI-only running total the bulk flow never displays or submits.
  claimOwner: {
    onMonday?: number;
    onTuesday?: number;
    onWednesday?: number;
    onThursday?: number;
    onFriday?: number;
    onSaturday?: number;
    onSunday?: number;
    maxWeeklyCapacity?: number;
  };
  schedules: any;
  events: any;
  isStandby: boolean;
  repeatFor?: number;
  isAppendValues: {
    isAppendSchedules: string;
    isAppendLocations: string;
    isAppendTeamsOfficesRoles: string;
    isAppendAttributes: string;
    isAppendGroups: string;
  };
  copySourceResourceId?: string;
  dontCopy?: {
    general?: boolean;
    locations?: boolean;
    teamsOfficesRoles?: boolean;
    attributes?: boolean;
  };
}

export interface ResourceForm {
  generalInformation: {
    name: string;
    description?: string;
    externalResourceId: string;
    commuteTimeInMin?: number;
    wipLimit?: number;
    email: string;
    phone?: string;
    claimCapacity?: number;
    virtualClaimCapacity?: number;
    complexLossClaimCapacity?: number;
    efficiency?: number;
    isWorkTeam?: boolean;
    lockImmediately?: boolean;
    cwsUserId?: string;
    isAlwaysAvailable?: boolean;
  };
  locations: LocationForm[];
  teamsOfficesRoles: {
    teams: string[]; // list of integration guids
    offices: string[]; // list of integration guids
    roles: string[]; // list of integration guids
    groups?: string[];
    isContingent?: boolean;
    contingentCompany?: string | null;
  };
  attributes: AttributeForm[];
  certsAndLicenses: CertAndLicensesForm[];
  addSelectedAttributeGUID: string;
  isNewResource: boolean;
  selectedAttributeGroups: SelectedAttributeGroup[];
  attributeFilterSelection?: AttributeSelection;
  claimOwner: {
    onMonday: number;
    onTuesday: number;
    onWednesday: number;
    onThursday: number;
    onFriday: number;
    onSaturday: number;
    onSunday: number;
    calculatedSumOfCapacities: number;
    maxWeeklyCapacity: number;
  };
  /**
   * Per-resource form selector (ADO #94651). Optional on the form because a flag-off tenant
   * never renders the control; ResourceFormHelper defaults it to 'STATIC' when the tenant flag
   * is on so a write always states the mode explicitly rather than leaving it unspecified.
   */
  fieldCapacityMode?: FieldCapacityMode;
  fieldCapacityByDay: {
    onMonday: number;
    onTuesday: number;
    onWednesday: number;
    onThursday: number;
    onFriday: number;
    onSaturday: number;
    onSunday: number;
  };
}

/**
 * The bulk/single-resource form's editable shape for a boolean attribute. The
 * "Active" checkbox edits ONE boolean, so the form models a single object here
 * even though the backend DTO (`SelectedAttributeValuesBools`) is an array. The
 * single object is normalized to the array at the submit boundary.
 */
export type SelectedAttributeBoolForm = {
  integrationGUID: string;
  selectedAttributeDefinitionIntegrationGUID: string;
  attributeDefinitionValueIntegrationGUID: string;
  title: string;
  displayName?: string;
  value?: boolean;
  startDate?: string;
  endDate?: string;
};

export interface AttributeForm {
  integrationGUID: string;
  displayName?: string;
  attributeDefinitionIntegrationGUID: string;
  selectedAttributeValuesVarchars: SelectAttributeValuesVarcharsForm[];
  selectedAttributeValuesBools: SelectedAttributeBoolForm;
}

export interface CertAndLicensesForm {
  integrationGUID: string;
  certificationAndLicenseIntegrationGUID: string;
  licenseNumber: string;
  verificationStatus: string;
  documentCount: number;
  issueDate: string;
  expirationDate: string;
}

export interface SelectAttributeValuesVarcharsForm {
  integrationGUID?: string;
  selectedAttributeDefinitionIntegrationGUID: string;
  attributeDefinitionValueIntegrationGUID: string;
  title: string;
  value: string | undefined;
  displayName?: string;
  startDate?: string;
  endDate?: string;
  subAttributes?: SubAttribute[];
}

export const resourceFormDefaults: ResourceForm = {
  generalInformation: {
    name: '',
    externalResourceId: '',
    email: '',
    phone: '',
    commuteTimeInMin: 0,
    isWorkTeam: false,
    lockImmediately: false,
  },
  locations: [],
  teamsOfficesRoles: {
    teams: [],
    offices: [],
    roles: [],
    groups: [],
    isContingent: false,
  },
  attributes: [],
  certsAndLicenses: [],
  addSelectedAttributeGUID: '',
  isNewResource: false,
  selectedAttributeGroups: [],
  attributeFilterSelection: AttributeSelection.Single,
  claimOwner: {
    onMonday: 1,
    onTuesday: 0,
    onWednesday: 0,
    onThursday: 0,
    onFriday: 0,
    onSaturday: 0,
    onSunday: 0,
    maxWeeklyCapacity: 0,
    calculatedSumOfCapacities: 0,
  },
  fieldCapacityByDay: {
    onMonday: 0,
    onTuesday: 0,
    onWednesday: 0,
    onThursday: 0,
    onFriday: 0,
    onSaturday: 0,
    onSunday: 0,
  },
};

export const validateUniqueActiveDays = (
  value: any,
  context: yup.TestContext
) => {
  if (!Array.isArray(value)) return true; // If value is not an array, skip validation

  const days = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];

  const values = context as any;

  const validate = values.originalValue.flatMap(
    (location: any, index: number) => {
      let returnError = false;

      //Check if any of the locations are active at the same time
      values.originalValue.flatMap((location2: any, index2: number) => {
        let datesOverlap = false;

        //if starting location, days can overlap
        if (
          (!location2.serviceInformation?.isStartingLocation &&
            !location.serviceInformation?.isStartingLocation) ||
          (location2.serviceInformation?.isStartingLocation &&
            location.serviceInformation?.isStartingLocation)
        ) {
          // try setting time to 0 then compare
          const location1FormatedStart = new Date(
            location.serviceInformation?.startDate || ''
          ).setHours(0, 0, 0, 0);
          const location1FormatedEnd = new Date(
            location.serviceInformation?.endDate || ''
          ).setHours(0, 0, 0, 0);
          const location2FormatedStart = new Date(
            location2.serviceInformation?.startDate || ''
          ).setHours(0, 0, 0, 0);

          if (index !== index2) {
            if (!location1FormatedEnd) {
              if (location2FormatedStart >= location1FormatedStart) {
                datesOverlap = true;
              }
            }
            if (
              location2FormatedStart <= location1FormatedEnd &&
              location2FormatedStart >= location1FormatedStart
            ) {
              datesOverlap = true;
            }

            if (datesOverlap) {
              days.forEach((day) => {
                if (
                  location2.serviceInformation[day] &&
                  location.serviceInformation[day]
                ) {
                  returnError = true;
                }
              });
            }
          }
        }
      });

      if (returnError) {
        return context.createError({
          message: 'Two locations cannot be active on the same days',
        });
      }

      return [];
    }
  );

  if (validate.length > 0) {
    return validate[0];
  }

  return true;
};

// Shared group-attribute validation reused by both the single-resource
// (resourceFormSchema) and bulk-update (bulkResourceFormSchema) forms so the
// two cannot drift.
export const selectedAttributeGroupsSchema = yup
  .array()
  .of(
    yup
      .object({
        id: yup.string(),
        displayName: yup.string(),
        startDate: yup.string().nullable(),
        endDate: yup
          .string()
          .nullable()
          .test(
            'end-after-start',
            'End date must be on or after start date',
            function (value) {
              const { startDate } = this.parent as {
                startDate?: string | null;
              };

              if (!startDate || !value) {
                return true;
              }

              return new Date(value) >= new Date(startDate);
            }
          ),
      })
      .test(
        'start-required-if-end',
        'Start date is required if end date is set',
        function (value) {
          const startDate = value?.startDate ?? null;
          const endDate = value?.endDate ?? null;

          if (!endDate) {
            return true;
          }

          if (!startDate) {
            return this.createError({
              path: `${this.path}.startDate`,
              message: 'Start date is required if end date is set',
            });
          }

          return true;
        }
      )
  )
  .nullable();

export const resourceFormSchema = yup.object().shape({
  generalInformation: yup.object({
    name: yup.string().required('Name is required'),
    description: yup
      .string()
      .max(100, 'Description cannot exceed 100 characters'),
    externalResourceId: yup.string().required('Resource ID is required'),
    commuteTimeInMin: yup.number().transform((value) => (value ? value : 0)),
    wipLimit: yup
      .number()
      .transform((_, val) => Number(val) || null)
      .nullable(),
    email: yup.string().email().required('Email is required'),
    phone: yup.string().nullable(),
    claimCapacity: yup
      .number()
      .min(0, 'Claim capacity must be non-negative')
      .transform((_, val) => (val === '' ? null : Number(val)))
      .nullable(),
    virtualClaimCapacity: yup
      .number()
      .min(0, 'Claim capacity must be non-negative')
      .transform((_, val) => (val === '' ? null : Number(val)))
      .nullable(),
    complexLossClaimCapacity: yup
      .number()
      .min(0, 'Claim capacity must be non-negative')
      .transform((_, val) => (val === '' ? null : Number(val)))
      .nullable(),
    efficiency: yup
      .number()
      .min(1, 'Efficiency must be greater than 0')
      .transform((_, val) => Number(val) || null)
      .nullable()
      .required('Efficiency is required'),
  }),
  locations: yup
    .array()
    .of(
      yup.object({
        integrationGUID: yup.string(),
        locationInformation: yup.object({
          name: yup.string().required('Name is required'),
          street: yup
            .string()
            .nullable()
            .when('isVirtualLocation', {
              is: true,
              then: yup
                .string()
                .transform(() => null)
                .nullable(),
            }),
          unit: yup
            .string()
            .nullable()
            .when('isVirtualLocation', {
              is: true,
              then: yup
                .string()
                .transform(() => null)
                .nullable(),
            }),
          city: yup
            .string()
            .nullable()
            .when('isVirtualLocation', {
              is: true,
              then: yup
                .string()
                .transform(() => null)
                .nullable(),
            }),
          state: yup.string().required('State is required'),
          zip: yup
            .string()
            .required('Zip is required')
            .test(
              'valid-zip-format',
              'Zip code must be 5 digits or 5-4 format (e.g., 12345 or 12345-6789)',
              (value) => {
                if (!value) return true;
                // Allow any valid zip format: 5 digits or 5-4 format
                const zipRegex = /^\d{5}(-\d{4})?$/;
                return zipRegex.test(value);
              }
            ),
          lat: yup.string().nullable(),
          long: yup.string().nullable(),
          isVirtualLocation: yup
            .boolean()
            .transform((_, val: string) => JSON.parse(val)),
        }),
        serviceInformation: yup.object().shape({
          startDate: yup
            .string()
            .nullable()
            .required('Start Date is required')
            .test(
              'validate-year',
              'Start date has an invalid year',
              (value) => {
                if (!value) return true;
                const year = new Date(value).getFullYear();
                return year.toString().startsWith('20');
              }
            ),
          endDate: yup
            .string()
            .nullable()
            .test(
              'test-date-range',
              'Start date must be before or equal to the end date',
              (value, context) => {
                if (!value) {
                  return true;
                }
                const startDate = new Date(context.parent.startDate);
                const endDate = new Date(value);
                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(0, 0, 0, 0);

                return startDate <= endDate;
              }
            )
            .test('validate-year', 'End date has an invalid year', (value) => {
              if (!value) return true;
              const year = new Date(value).getFullYear();
              return year.toString().startsWith('20');
            })
            .when('isStartingLocation', {
              is: true,
              then: yup
                .string()
                .nullable()
                .test(
                  'test-date-range',
                  'Start date must be before or equal to the end date',
                  (value, context) => {
                    if (!value) {
                      return true;
                    }
                    const startDate = new Date(context.parent.startDate);
                    const endDate = new Date(value);
                    startDate.setHours(0, 0, 0, 0);
                    endDate.setHours(0, 0, 0, 0);

                    return startDate <= endDate;
                  }
                ),
            }),
          allowedTravelRadius: yup
            .number()
            .min(0, 'Claim capacity must be non-negative')
            .transform((_, val: string) => Number(val) || 0)
            .required('Travel Radius is required')
            .when('isVirtualLocation', {
              is: true,
              then: yup
                .number()
                .transform(() => 0)
                .nullable(),
            }),
          monday: yup.boolean(),
          tuesday: yup.boolean(),
          wednesday: yup.boolean(),
          thursday: yup.boolean(),
          friday: yup.boolean(),
          saturday: yup.boolean(),
          sunday: yup.boolean(),
          isStartingLocation: yup
            .boolean()
            .nullable()
            .when('isVirtualLocation', {
              is: true,
              then: yup
                .boolean()
                .transform(() => false)
                .nullable(),
            }), // this is in serviceInformation for yup validation
          isVirtualLocation: yup
            .boolean()
            .transform((_, val: string) => JSON.parse(val)),
          exclusionZones: yup
            .object({
              states: yup.array().of(yup.string()).nullable(),
              counties: yup.array().of(yup.string()).nullable(),
              zipCodes: yup.array().of(yup.string()).nullable(),
            })
            .nullable(),
        }),
      })
    )
    .test(
      'unique-active-days',
      'Two locations cannot be active on the same days',
      validateUniqueActiveDays
    ),
  attributes: yup.array().of(
    yup.object({
      integrationGUID: yup.string(),
      attributeDefinitionIntegrationGUID: yup.string(),
      displayName: yup.string(),
      selectedAttributeValuesVarchars: yup
        .array()
        .of(
          yup.object({
            integrationGUID: yup.string(),
            selectedAttributeDefinitionIntegrationGUID: yup.string(),
            attributeDefinitionValueIntegrationGUID: yup.string(),
            title: yup.string(),
            value: yup.string().required('Please select an attribute'),
            startDate: yup
              .string()
              .nullable()
              .when('endDate', {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                is: (endDate: any) => !!endDate,
                then: yup
                  .string()
                  .nullable()
                  .required('Start date is required if end date is set')
                  .test(
                    'test-date-range',
                    'Start date must be before end date',
                    (value, context) => {
                      if (!context.parent.endDate) {
                        return true;
                      }

                      const startDate = new Date(value?.toString() || '');
                      const endDateRaw = new Date(context.parent.endDate);

                      // Normalize end date to 11:59:59 PM
                      const endDate = endOfDay(endDateRaw);

                      return startDate < endDate;
                    }
                  ),
              }),
            endDate: yup.string().nullable(),
            subAttributes: yup.array().of(
              yup.object({
                displayName: yup.string().required('Field is required'),
                value: yup.string().nullable(),
                startDate: yup.string().required('Start date is required'),
                endDate: yup.string().required('End date is required'),
              })
            ),
          })
        )
        .nullable(),
      selectedAttributeValuesBools: yup
        .object({
          integrationGUID: yup.string(),
          selectedAttributeDefinitionIntegrationGUID: yup.string(),
          attributeDefinitionValueIntegrationGUID: yup.string(),
          title: yup.string(),
          value: yup.boolean(),
          startDate: yup
            .string()
            .nullable()
            .when('endDate', {
              is: true,
              then: yup
                .string()
                .nullable()
                .required('Start date is required if end date is set'),
            }),
          endDate: yup.string().nullable(),
        })
        .nullable(),
    })
  ),
  teamsOfficesRoles: yup.object({
    teams: yup.array().of(yup.string()),
    offices: yup.array().of(yup.string()),
    roles: yup.array().of(yup.string()),
    isContingent: yup.boolean().nullable(),
    contingentCompany: yup
      .string()
      .nullable()
      .when('isContingent', {
        is: true,
        then: yup
          .string()
          .nullable()
          .required('Contingent Company is Required'),
      }),
  }),
  certsAndLicenses: yup.array().of(
    yup.object({
      integrationGUID: yup.string(),
      certificationAndLicenseIntegrationGUID: yup.string(),
      licenseNumber: yup.string(),
      verificationStatus: yup.string(),
      documentCount: yup.number(),
      issueDate: yup.string(),
      expirationDAte: yup.string(),
    })
  ),
  addSelectedAttributeGUID: yup.string().nullable(),
  selectedAttributeGroups: selectedAttributeGroupsSchema,
  claimOwner: yup.object({
    onMonday: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Claim capacity must be non-negative'),
    onTuesday: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Claim capacity must be non-negative'),
    onWednesday: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Claim capacity must be non-negative'),
    onThursday: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Claim capacity must be non-negative'),
    onFriday: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Claim capacity must be non-negative'),
    onSaturday: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Claim capacity must be non-negative'),
    onSunday: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Claim capacity must be non-negative'),
    maxWeeklyCapacity: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Claim capacity must be non-negative')
      .test(
        'test-max-weekly-capacity',
        'Max Weekly Capacity must be less than or equal to the sum of daily capacities',
        (value, context) => {
          if (value === null || value === undefined) {
            return true;
          }
          const validation = value <= context.parent.calculatedSumOfCapacities;

          return validation;
        }
      ),
  }),
  fieldCapacityByDay: yup.object({
    onMonday: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Field capacity must be non-negative'),
    onTuesday: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Field capacity must be non-negative'),
    onWednesday: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Field capacity must be non-negative'),
    onThursday: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Field capacity must be non-negative'),
    onFriday: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Field capacity must be non-negative'),
    onSaturday: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Field capacity must be non-negative'),
    onSunday: yup
      .number()
      .transform((v) => (v === '' || Number.isNaN(v) ? 0 : v))
      .min(0, 'Field capacity must be non-negative'),
  }),
});

// for some reason react query wants the type of upsert to be like this.
export type UpsertResource = UseMutateFunction<
  | {
      success: boolean;
      message?: string;
    }
  | {
      success: boolean;
      message: string;
    },
  unknown,
  Resource,
  unknown
>;

export interface CertsAndLicenseEdit {
  verificationType: string;
  name: string;
  organization: string;
  description: string;
  stateProvince: StateProvince[];
  issueDate: string;
  expirationDate: string;
  mode: 'assign' | 'edit';
  isActive?: boolean;
  requireExpirationDate?: boolean;
  licenseNumber: string;
  resourceCertId: string;
  integrationGUID: string;
}

export interface ResourceTaskParams {
  skills: string[];
  availability: { startTime: string; endTime: string }[];
  role: string;
  languages: string[];
  location: {
    latitude: number;
    longitude: number;
    allowedTravelRadius: number;
  };
}

export enum SelectedAttributesDisplayName {
  LANGUAGES = 'Languages Spoken',
}

export interface States {
  state: string;
  abbreviation: string;
  province: string;
}

export interface Counties {
  name: string;
  state: string;
}

export interface GeoResolverResponse {
  state: string;
  stateCode: string;
  city: string;
  county: string | null;
}

export interface GeoResolverError {
  type: string;
  title: string;
  status: number;
  detail: string;
}

export type BulkUpdateExclusionZonesForm = {
  type: 'Resources' | 'Team' | 'Office';
  locations: [
    {
      exclusionZonesInformation: {
        states?: string[];
        counties?: MultiSelectOption[];
        zipCodes?: string[];
      };
    }
  ];
  ids: string[];
};

/**
 * A bulk-update attribute as it goes over the wire: identical to the form shape
 * except `selectedAttributeValuesBools` is normalized to the backend array type.
 */
export type BulkUpdateAttributePost = Omit<
  AttributeForm,
  'selectedAttributeValuesBools'
> & {
  selectedAttributeValuesBools: SelectedAttributeValuesBools[];
};

export type BulkUpdatePostBody = {
  target: {
    type: string;
    ids: string[];
  };
  copySourceResourceId?: string;
  attributes?: BulkUpdateAttributePost[];
  // Grouped-attribute bulk update. A resource is updated in
  // individual (attributes) mode XOR grouped (selectedAttributeGroups) mode.
  selectedAttributeGroups?: SelectedAttributeGroup[];
  attributeFilterSelection?: AttributeSelection;
  generalInformation?: {
    commuteTimeInMin?: number;
    wipLimit?: number;
    claimCapacity?: number;
    virtualClaimCapacity?: number;
    complexLossClaimCapacity?: number;
    efficiency?: number;
    isWorkTeam?: boolean;
    lockImmediately?: boolean;
  };
  // Partial: a copied source may set only some day fields (absence is dropped by
  // removeEmptyValues), so the payload is not required to carry the full shape.
  claimOwnerCapacity?: Partial<ClaimOwnerCapacity>;
  teamsOfficesRoles?: {
    isContingent?: boolean;
    contingentCompany?: string;
    teams?: string[];
    offices?: string[];
    roles?: string[];
  };
  exclusionZones?: {
    states?: string[];
    counties?: string[];
    zipCodes?: string[];
  };
  locations?: LocationForm[];
  schedules?: any;
  events?: any;
  isAppendValues?: {
    isAppendSchedules?: boolean;
    isAppendLocations?: boolean;
    isAppendTeamsOfficesRoles?: boolean;
    isAppendAttributes?: boolean;
    isAppendGroups?: boolean;
  };
};

export interface RenderWorkdaySectionProps {
  hasUpsertSchedulesPermissions?: boolean;
  handleOpenEditSchedule: () => void;
  renderAddedEvents: React.ReactNode;
  openEditSchedule: boolean;
  handleCloseEditSchedule: () => void;
  isEdit: boolean;
  permissions: Ability<[Actions, Subjects]> | undefined;
  resourceSetValue: UseFormSetValue<ResourceBulkForm>;
  resourceWatch: UseFormWatch<ResourceBulkForm>;
}

export interface RenderBlockedTimeSectionProps {
  resourceWatch: UseFormWatch<ResourceBulkForm>;
  resourceSetValue: UseFormSetValue<ResourceBulkForm>;
  handleCloseEditEvent: () => void;
  hasUpsertEventsPermissions?: boolean;
  handleOpenEditEvent: () => void;
  openEditEvent: boolean;
  isEdit: boolean;
  renderAddedEvents: React.ReactNode;
  permissions: Ability<[Actions, Subjects]> | undefined;
  allowedRepeat?: number;
  resourceCount?: number;
  resourceSetError?: UseFormSetError<ResourceBulkForm>;
}

export interface ResourceLocationPost {
  streetLine1: string;
  streetLine2: string | null;
  city: string;
  state: string;
  zip: string;
}

export interface SelectedAttributeValuesVarcharPost {
  selectedAttributeDefinitionIntegrationGUID: string;
  displayName: string;
  attributeDefinitionValueIntegrationGUID: string;
  integrationGUID: string;
  value: string;
  startDate: string | null;
  endDate: string | null;
}

export interface ResourceAttributePost {
  integrationGUID: string;
  displayName: string;
  attributeDefinitionIntegrationGUID: string;
  selectedAttributeValuesVarchars: SelectedAttributeValuesVarcharPost[];
  selectedAttributeValuesInts: unknown[]; // Replace unknown with actual type if available
  selectedAttributeValuesFloats: unknown[]; // Replace unknown with actual type if available
  selectedAttributeValuesBools: unknown[]; // Replace unknown with actual type if available
}

export interface QualifiedResourcePost {
  integrationGUID?: string;
  id: string;
  email: string;
  phone: string;
  name: string;
  locations: ResourceLocationPost;
  attributes: ResourceAttributePost[];
  distanceToTask: number;
}
